<template>
    <div class="warehouse-list-table">
        <el-table
            :data="warehouses"
            :empty-text="translate('messages.view.no_data')"
            highlight-current-row
        >
            <el-table-column
                width="150px"
                prop="codigoLyra"
                :label="translate('table.lyra_warehouse_id')"
                label-class-name="warehouse-list-table_label"
            />
            <el-table-column
                min-width="100px"
                prop="unidade"
                :label="translate('table.warehouse_legal_name')"
                label-class-name="warehouse-list-table_label"
            >
                <template v-slot:default="scope">
                    <div class="warehouse-list_text">
                        {{ scope.row.unidade }}
                    </div>
                </template>
            </el-table-column>
            <el-table-column
                width="180px"
                prop="habilitacaoSenave"
                :label="translate('table.senave')"
                label-class-name="warehouse-list-table_label"
            >
                <template v-slot:default="scope">
                    <div class="transaction-list_text">
                        {{ scope.row.habilitacaoSenave }}
                    </div>
                </template>
            </el-table-column>
            <el-table-column
                min-width="150px"
                prop="localidade"
                :label="translate('table.location')"
                label-class-name="warehouse-list-table_label"
            >
                <template v-slot:default="scope">
                    <div class="warehouse-list_text">
                        {{ scope.row.localidade }}
                    </div>
                </template>
            </el-table-column>
            <el-table-column
                width="150px"
                align="center"
                :label="translate('table.available_capacity')"
                label-class-name="warehouse-list-table_label"
            >
                <template v-slot:default="scope">
                    <div class="warehouse-list_text">
                        {{ formatTonValue(scope.row.saldoAtualTonFormatado) }}
                    </div>
                </template>
            </el-table-column>
            <el-table-column
                width="150px"
                align="center"
                header-class-name="custom-header"
                :label="translate('table.capacity_in_transit')"
                label-class-name="warehouse-list-table_label"
            >
                <template v-slot:default="scope">
                    <div class="warehouse-list_text">
                        {{ formatTonValue(scope.row.saldoTransitoTonFormatado) }}
                    </div>
                </template>
            </el-table-column>
            <el-table-column
                width="120px"
                :label="translate('table.actions')"
                align="center"
                label-class-name="warehouse-list-table_label"
            >
                <template v-slot:default="scope">
                    <el-dropdown
                        class="action-button"
                        trigger="click"
                        @click="scope"
                    >
                        <span class="el-dropdown-link">
                            <i class="mdi mdi-dots-vertical" />
                        </span>
                        <el-dropdown-menu v-slot="dropdown">
                            <el-dropdown-item
                                icon="el-icon-view"
                                @click.native="handleView(scope.row)"
                            >
                                {{ translate('messages.view_warehouse') }}
                            </el-dropdown-item>
                            <el-dropdown-item
                                icon="el-icon-map-location"
                                @click.native="openViewTerritoriesModal(scope.row)"
                            >
                                {{ translate('messages.view.view_territories') }}
                            </el-dropdown-item>
                        </el-dropdown-menu>
                    </el-dropdown>
                </template>
            </el-table-column>
        </el-table>
        <div class="warehouse-list-table__pagination">
            <div class="total">
                <span
                    >{{ totalElements }}
                    {{
                        translate('messages.view.results')
                    }}</span
                >
                <div style="margin: 0 20px"></div>
                <span
                    >{{
                        translate('messages.view.views_by_page')
                    }}:</span
                >
                <el-pagination
                    @size-change="handleSizeChange"
                    :current-page="page"
                    :page-sizes="[10, 20, 30, 40]"
                    :page-size="size"
                    layout="sizes"
                    :total="totalElements"
                />
            </div>
            <div class="prev_next">
                <el-pagination
                    @current-change="handleCurrentChange"
                    :current-page.sync="page"
                    :page-sizes="[10, 20, 30, 40]"
                    :page-size="size"
                    layout="prev, pager, next, jumper"
                    :total="totalElements"
                />
            </div>
        </div>
        <view-territories-modal
            v-if="showViewTerritoriesModal"
            :show="showViewTerritoriesModal"
            :warehouse="selectedWarehouse"
            @close="showViewTerritoriesModal = false"
        />
    </div>
</template>

<script>
import warehouseService from '@/services/ArmazemModuleService';
import ViewTerritoriesModal from '@/modules/warehouse/views/components/ViewTerritoriesModal.vue';
import formatters from '@/utils/formatters';

import { mapState } from 'vuex';
import { MainPageTabs } from '@/modules/warehouse/constants/MainPageTabsEnum';

export default {
    name: 'warehouseDefault',
    components: {
        ViewTerritoriesModal
    },
    props: {
        filters: {
            type: Object,
            required: false,
            default: () => null
        }
    },
    data() {
        return {
            warehouses: [],
            page: 1,
            size: 10,
            totalElements: 0,
            showViewTerritoriesModal: false,
            selectedWarehouse: null,
            tabName: MainPageTabs.WAREHOUSES_TAB
        };
    },
    computed: {
        ...mapState('warehouseModule', ['selectedTab', 'tableLoadControl'])
    },
    mounted() {
        if (this.selectedTab === this.tabName && !this.tableLoadControl.warehouseList) {
            this.getWarehouses();
            this.$store.commit('warehouseModule/SET_TABLE_CONTROL', {warehouseList: true});
        }
    },
    watch: {
        filters: {
            handler(newVal) {
                if (this.selectedTab === this.tabName && !this.tableLoadControl.warehouseList) {
                    this.getWarehouses();
                    this.$store.commit('warehouseModule/SET_TABLE_CONTROL', {warehouseList: true});
                }
            },
            deep: true
        },
        selectedTab: {
            handler(newVal) {
                if (this.selectedTab === this.tabName && !this.tableLoadControl.warehouseList) {
                    this.getWarehouses();
                    this.$store.commit('warehouseModule/SET_TABLE_CONTROL', {warehouseList: true});
                }
            }
        }
    },
    methods: {
        translate(key, params = {}) {
            const base = 'warehouse';
            return this.$t(`${base}.${key}`, params);
        },
        handleSizeChange(size) {
            this.page = 1;
            this.size = size;
            this.getWarehouses();
        },
        handleCurrentChange(page) {
            this.page = page;
            this.getWarehouses();
        },
        async getWarehouses() {
            if (this.filters) {
                await warehouseService.getWarehousesPaginated(
                    {searchText: this.filters.searchText},
                    (this.page - 1), this.size
                ).then(({ data }) => {
                    this.warehouses = data.itens;
                    this.totalElements = data.totalItens;
                });
            }
        },
        handleView(selected) {
            this.$router.push({
                name: 'VisualizarArmazem',
                params: {
                    warehouseId: selected.id,
                    viewPendingOperations: 'transacoes-armazem',
                    type: 'WAREHOUSE'
                }
            });
        },
        openViewTerritoriesModal(row) {
            this.showViewTerritoriesModal = true;
            this.selectedWarehouse = row;
        },
        formatTonValue(value) {
            if (value !== 0 && (!value || value === '')) return '-';
            return this.formatNumberToLocale(value);
        },
        formatNumberToLocale(value) {
            return formatters.minFormatNumber(value, 3, this.$i18n.locale);
        }
    }
};
</script>

<style scoped lang="scss">
.warehouse-list-table {
    margin-top: -8px;
    .el-table {
        border-radius: 5px;
        height: calc(100% - 60px) !important;
        width: 100%;

        .action-button {
            cursor: pointer;
        }
    }
    .warehouse-list-table__pagination {
        display: grid;
        grid-template-columns: 2fr auto;
        margin: 20px 12px 0;
        @media (max-width: 768px) {
            grid-template-columns: 1fr;
        }

        .total {
            display: flex;
            align-items: center;
            @media (max-width: 768px) {
                flex-direction: column;
            }
        }

        .prev_next {
            display: flex;
            @media (max-width: 768px) {
                justify-content: center;
                margin-top: 20px;
            }
        }
    }
}
.level1-label {
    font-size: 12px;
    color: #7c7c7c !important;
}
::v-deep .warehouse-list-table_label {
    word-break: keep-all !important;
}
.warehouse-list_text {
    word-break: keep-all !important;
}
</style>
